import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHeart, FiUsers, FiAward, FiTarget } = FiIcons;

const About = () => {
  const team = [
    {
      name: 'Mandla FG Cetywayo',
      role: 'Creative Director',
      description: 'With over 12 years in brand strategy, Mandla leads our creative vision with a focus on authentic South African storytelling.',
      image: 'https://media.phst.co.za/file/ph/creativesol-mandla.jpg?f=auto&w=600&h=800&fit=fill&gravity=north'
    },
    {
      name: 'Babalwa Sibiya',
      role: 'Senior Designer',
      description: "Babalwa's expertise in digital design has helped over 100 local businesses establish their online presence.",
      image: 'https://media.phst.co.za/file/ph/creativesol-babalwa2.jpg?f=auto&w=600&h=800&fit=fill&gravity=north'
    },
    {
      name: 'Thandiwe Khedama',
      role: 'Brand Strategist',
      description: 'Thandiwe brings deep market insights and cultural understanding to every branding project.',
      image: 'https://media.phst.co.za/file/ph/creativesol-thandiwe.jpg?f=auto&w=600&h=800&fit=fill&gravity=north'
    },
    {
      name: 'Sipho Ndlovu',
      role: 'Web Developer',
      description: 'Combining technical excellence with creative vision, Sipho ensures our designs come to life flawlessly online.',
      image: 'https://media.phst.co.za/file/ph/creativesol-sipho2.jpg?f=auto&w=600&h=800&fit=fill&gravity=north'
    }
  ];

  const values = [
    {
      icon: FiTarget,
      title: 'Innovation',
      description: 'We constantly push creative boundaries to deliver fresh, innovative solutions that set our clients apart.'
    },
    {
      icon: FiUsers,
      title: 'Collaboration',
      description: 'We believe the best results come from working closely with our clients as true partners in their success.'
    },
    {
      icon: FiAward,
      title: 'Excellence',
      description: 'We maintain the highest standards in everything we do, from initial concept to final delivery.'
    },
    {
      icon: FiHeart,
      title: 'Ubuntu',
      description: 'We embrace the African philosophy of Ubuntu - we are who we are through others, fostering community and connection.'
    }
  ];

  const achievements = [
    {
      title: 'Google Partner Agency',
      description: 'Certified Google Partner for digital marketing excellence'
    },
    {
      title: 'Loerie Awards Finalist 2022',
      description: 'Recognized for outstanding creative work in brand identity'
    },
    {
      title: 'SA Web Awards Winner 2021',
      description: 'Best website design for e-commerce category'
    }
  ];

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="font-poppins font-bold text-4xl md:text-5xl lg:text-6xl mb-6">
              About <span className="text-purple-600">Creative Solutions</span>
            </h1>
            <p className="font-inter text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
              Founded in Johannesburg in 2013, Creative Solutions Agency has grown from a small design studio to one of South Africa's most trusted creative partners.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-6">
                Our Story
              </h2>
              <p className="font-inter text-lg text-gray-600 mb-6 leading-relaxed">
                We believe that great design isn't just about looking good – it's about solving problems and creating connections. What started as a passion project in a small Johannesburg studio has evolved into a comprehensive creative agency that serves clients across South Africa and beyond.
              </p>
              <p className="font-inter text-lg text-gray-600 mb-6 leading-relaxed">
                Our journey has been driven by a simple belief: every business has a unique story to tell, and our job is to help them tell it in the most compelling way possible. Through authentic South African storytelling and world-class design, we've helped hundreds of businesses transform their brands and achieve their goals.
              </p>
              <p className="font-inter text-lg text-gray-600 leading-relaxed">
                Today, we're proud to be recognized as one of the leading creative agencies in South Africa, but we never forget our roots or the values that got us here.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-w-4 aspect-h-3">
                <img
                  src="https://media.phst.co.za/file/ph/creativesol-about.jpg"
                  alt="Our team at work"
                  className="w-full h-96 object-cover rounded-2xl shadow-xl"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-purple-600 rounded-2xl flex items-center justify-center">
                <div className="text-center text-white">
                  <div className="font-poppins font-bold text-2xl">10+</div>
                  <div className="font-inter text-sm">Years</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Team */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Heading */}
          <div className="text-center mb-16">
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4">
              Meet Our Team
            </h2>
            <p className="font-inter text-lg text-gray-600 max-w-2xl mx-auto">
              Our diverse team of creative professionals brings together years of
              experience and a passion for exceptional design.
            </p>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member) => (
              <div
                key={member.name}
                className="bg-white rounded-xl overflow-hidden shadow-lg card-hover"
              >
                {/* Image container */}
                <div className="overflow-hidden h-52 rounded-t-xl">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="font-poppins font-semibold text-xl text-gray-900 mb-1">
                    {member.name}
                  </h3>
                  <p className="font-inter text-purple-600 font-medium mb-3">
                    {member.role}
                  </p>
                  <p className="font-inter text-gray-600 text-sm leading-relaxed">
                    {member.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4">
              Our Values
            </h2>
            <p className="font-inter text-lg text-gray-600 max-w-2xl mx-auto">
              These core values guide everything we do and shape how we work with our clients and each other.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-teal-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={value.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-poppins font-semibold text-xl text-gray-900 mb-3">
                  {value.title}
                </h3>
                <p className="font-inter text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-teal-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white mb-4">
              Certifications & Awards
            </h2>
            <p className="font-inter text-lg text-gray-100 max-w-2xl mx-auto">
              Our commitment to excellence has been recognized by industry leaders and award bodies across South Africa.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {achievements.map((achievement, index) => (
              <motion.div
                key={achievement.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 text-center shadow-lg"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiAward} className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-poppins font-semibold text-xl text-gray-900 mb-3">
                  {achievement.title}
                </h3>
                <p className="font-inter text-gray-600">
                  {achievement.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;